
import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, effect, inject, Injector, input, runInInjectionContext, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { EventBus, EventBusBase, RoutingService } from '@skylab-angular-shared/core';
import { CompanyContextLink } from '@skylab-angular-shared/pipes';
import { filter } from 'rxjs';
import { composeMenuItems, getMenuItems, MenuItem } from './navigation.model';

@Component({
	selector: 'skl-navigation',
	template: `
		<nav id="skl-sidebar" class="sidebar" role="navigation" [class.open]="isOpen()" [class.no-transition]="resizing()">
			<ul class="items-container">
				@for (menuItem of menuItems(); track menuItem.id) {
					<li [class.mt-auto]="menuItem.isBottom">
						@if (menuItem.list?.length) {
							<details
								id="{{ menuItem.id }}"
								class="expansion-panel"
								[open]="!menuItem.closed"
								(toggle)="onDetailsToggle($event, menuItem)"
							>
								<summary
									id="{{ menuItem.id }}-summary"
									(click)="onSummaryClick($event, menuItem)" 
									(keydown.control.space)="onSummaryKeyboard($event, menuItem)" 
									(keydown.control.shift.space)="onSummaryKeyboard($event, menuItem)"
								>
									<ng-container *ngTemplateOutlet="menuItemTemplate; context: { $implicit: menuItem, hasChildren: true }" />
								</summary>
								<ul class="sub-group">
									@for (listMenuItem of menuItem.list; track listMenuItem.id) {
										<li>
											<ng-container *ngTemplateOutlet="menuItemTemplate; context: { $implicit: listMenuItem }" />
										</li>
									}
								</ul>
							</details>
						} @else {
							<ng-container *ngTemplateOutlet="menuItemTemplate; context: { $implicit: menuItem }" />
						}
					</li>
				}
			</ul>
		</nav>

		<div class="backdrop" [class.open]="isOpen()" aria-hidden="true" (click)="hide()"></div>

		<ng-template #menuItemTemplate let-menuItem let-hasChildren="hasChildren">
			<div class="nav-item" [class.active]="isMenuItemActive(menuItem)">
				<a
					id="{{ menuItem.id }}-link"
					class="nav-link"
					[routerLink]="menuItem.hrefObj.url | sklCompanyLink"
					[queryParams]="menuItem.hrefObj.params"
				>
					@if (menuItem.icon) {
						<ins-icon size="20px" [name]="menuItem.icon" />
					}

					<div class="nav-label">{{ menuItem.label }}</div>
				</a>

				@if (menuItem.addHrefObj) {
					<a
						id="{{ menuItem.id }}-add-button"
						class="nav-action add-btn"
						[routerLink]="menuItem.addHrefObj.url | sklCompanyLink"
						[queryParams]="menuItem.addHrefObj.params"
					>
						<ins-icon name="add-regular" />
					</a>
				}

				@if (hasChildren) {
					<div
						id="{{ menuItem.id }}-more-button"
						class="nav-action"
					>
						<ins-icon class="skl-anim" [class.rotate-in]="menuItem.closed" name="chevron-up" />
					</div>
				}

			</div>
		</ng-template>
	`,
	styles: `
		:host {
			--_sidebar-background-color: light-dark(var(--surface-1-color-dark), var(--surface-2-color-dark));
			--_nav-item-color: oklch(80% 0 0);
			--_nav-item-color-hover: oklch(from var(--_nav-item-color) l c h / 90%);
			--_nav-item-action-color-hover: oklch(from var(--_nav-item-color) l c h / 20%);
			--_nav-item-color-background-active: oklch(26.15% 0 0);
			--_add-button-color: oklch(52.88% 0.096 218.42);
			--_add-button-color-hover: oklch(62.6% 0.106 216.32);
			--_sidebar-padding: var(--gutter-small);
			--_backdrop-color: oklch(0% 0 0 / 32%);
			--_focus-outline-color: oklch(from var(--_add-button-color) l c h);
			--_nav-item-size: 2.25rem;
			--_app-bar-size: 3.25rem;

			interpolate-size: allow-keywords;
			font-size: 0.875rem;
			display: block;
			block-size: 100%;

			a {
				all: unset;
			}
		}

		@supports (block-size: calc-size(auto, size)) {
			.expansion-panel {
				&:open {
					&::details-content {
						block-size: calc-size(auto, size);
						opacity: 1;
					}
				}

				&::details-content {
					overflow: hidden;
					opacity: 0;
					block-size: 0;
					transition:
						content-visibility 0.5s ease,
						opacity 0.3s ease-in-out,
						block-size 0.3s ease;
					transition-behavior: allow-discrete;
				}
			}
		}

		details {
			display: grid;
			grid-template-rows: min-content;
			transition: grid-template-rows 300ms ease-out;
			
			& > summary {
				list-style: none;

				&::-webkit-details-marker {
					display: none;
				}

				&:focus-visible {
					outline: none;

					.nav-item {
						color: var(--text-color-light);
						background: linear-gradient(to right, oklch(from var(--_nav-item-color-background-active) l c h / 10% ), var(--_nav-item-color-background-active) 100%);
						border-color: oklch(from var(--_nav-item-color-background-active) 35% c h );

						.nav-action {
							background: var(--_nav-item-action-color-hover);
							color: var(--text-color-light);
						}
					}
				}
			}
		}

		.skl-anim {
			transition: transform 0.3s ease;
		}

		.rotate-in {
			transform: rotate(180deg);
		}

		.mt-auto {
			margin-block-start: auto;
			padding-block: var(--gutter-medium);
			border-block-start: 1px solid oklch(from var(--_nav-item-color-background-active) l c h / 90%);
		}

		.sidebar {
			display: flex;
			flex-direction: column;
			inline-size: 0;
			block-size: 100%;
			background: var(--_sidebar-background-color);
			overflow-y: auto;
			overflow-x: hidden;
			transform-origin: 0 0;
			transform: translateZ(0);
			will-change: transform, inline-size;
			transition: inline-size 0.3s cubic-bezier(0.77, 0.2, 0.05, 1);

			&.open {
				inline-size: 15rem;
			}

			&.no-transition {
				transition: none !important;
			}

			.items-container {
				display: flex;
				flex-direction: column;
				padding-block: var(--_sidebar-padding);
				gap: var(--gutter-tiny);
				list-style: none;
				inline-size: 15rem;
			}

			.nav-item {
				display: flex;
				position: relative;
				color: var(--text-minor-color-light);
				flex-direction: row;
				align-items: center;
				block-size: var(--_nav-item-size);
				padding-inline-start: var(--gutter-medium);
				margin-inline: var(--gutter-small);
				border: 1px solid transparent;
				border-radius: 6px;
				overflow: clip;
				transition: all 0.3s ease;

				&:hover,
				&:focus-visible,
				&:focus-within,
				&.active {
					color: var(--text-color-light);
					background: linear-gradient(to right, oklch(from var(--_nav-item-color-background-active) l c h / 10% ), var(--_nav-item-color-background-active) 100%);
					border-color: oklch(from var(--_nav-item-color-background-active) 35% c h );
					outline: none;

					.nav-action {
						color: var(--text-color-light);
					}
				}
			}

			.sub-group {
				display: flex;
				flex-direction: column;
				gap: var(--gutter-tiny);
				margin-block-start: var(--gutter-tiny);
				list-style: none;

				.nav-item {
					padding-inline-start: var(--gutter-small);
				}
			}

			.nav-label {
				display: flex;
				padding-inline-start: var(--gutter-medium);
			}

			.nav-action {
				display: flex;
				align-items: center;
				justify-content: center;
				color: var(--text-minor-color-light);
				block-size: 100%;
				aspect-ratio: 1;
				cursor: pointer;
				user-select: none;
				transition: all 0.3s ease;

				&:hover,
				&:focus-visible {
					background: var(--_nav-item-action-color-hover);
					color: var(--text-color-light);
					outline: none;
				}
			}

			.nav-link {
				display: flex;
				flex: 1;
				block-size: 100%;
				align-items: center;
				cursor: pointer;
				outline: none;
			}
		}

		.backdrop {
			display: none;
		}

		@media only screen and (max-width: 60rem) {
			.sidebar {
				position: absolute;
				inset-block-start: 0;
				inset-inline-start: 0;
				block-size: 100%;
				inline-size: 250px;
				transform: translate(-100%, 0);
				transition: transform 0.5s cubic-bezier(0.77, 0.2, 0.05, 1);
				z-index: 102;
				pointer-events: all;
				will-change: transform;

				&.open {
					transform: none;
					inline-size: 250px;
				}

				.items-container {
					inline-size: 100%;
				}
			}

			.backdrop {
				display: block;
				position: fixed;
				inset: 0;
				background: var(--_backdrop-color);
				z-index: 101;
				opacity: 0;
				pointer-events: none;
				transition: opacity 0.3s ease;

				&.open {
					opacity: 1;
					pointer-events: all;
				}
			}
		}

	`,
	host: {
		'(document:keydown.escape)': 'onEscape()',
		'(window:resize)': 'onResize()',
	},
	imports: [NgTemplateOutlet, RouterLink, CompanyContextLink],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationComponent {
	readonly #routingService = inject(RoutingService);
	readonly #router = inject(Router);
	readonly #eventBus = inject(EventBus) as EventBusBase;
	readonly #destroyRef = inject(DestroyRef);
	readonly #injector = inject(Injector);

	readonly singleOpenMode = input(false);


	readonly #mobileQuery = window.matchMedia('(max-width: 60rem)');
	readonly #open = signal(!this.#mobileQuery.matches);
	protected readonly resizing = signal(false);
	protected readonly menuItems = signal<MenuItem[]>([]);
	protected readonly navigationEvent = toSignal(this.#router.events.pipe(filter(e => e instanceof NavigationEnd)));
	public readonly isOpen = this.#open.asReadonly();

	readonly #menuItemsDefinition = getMenuItems();
	readonly #recentlyCollapsed = new Set<string>();

	#resizeTimer: ReturnType<typeof setTimeout> | null = null;

	constructor() {
		Object.values(this.#menuItemsDefinition).forEach((config: any) => {
			if (config.list && config.closed === undefined) {
				config.closed = !this.#loadMenuState(config.id);
			}
		});

		const onBreakpointChange = (e: MediaQueryListEvent) => {
			this.#open.set(!e.matches);
		};
		this.#mobileQuery.addEventListener('change', onBreakpointChange);

		const observerId = this.#eventBus.observe('company-refresh', () => {
			runInInjectionContext(this.#injector, () => {
				this.#updateMenuAsync()
			})
		});
		this.#destroyRef.onDestroy(() => {
			this.#mobileQuery.removeEventListener('change', onBreakpointChange);
			this.#eventBus.deleteObserver([observerId]);
		});

		effect(() => {
			this.navigationEvent();
			untracked(() => {
				if (this.#mobileQuery.matches) {
					this.#open.set(false);
				}

				const items = this.menuItems();
				if (items.length) {
					this.#expandActiveSections(items);
				}
			});
		});

		this.#updateMenuAsync();
	}

	public setOpen(value: boolean) {
		this.#open.set(value);
	}

	public toggle() {
		this.#open.update(v => !v);
	}

	protected onResize() {
		this.resizing.set(true);
		if (this.#resizeTimer) clearTimeout(this.#resizeTimer);
		this.#resizeTimer = setTimeout(() => this.resizing.set(false), 150);
	}

	protected hide() {
		this.#open.set(false);
	}

	protected onEscape() {
		if (this.#mobileQuery.matches) {
			this.hide();
		}
	}

	protected onDetailsToggle(event: Event, menuItem: MenuItem) {
		const details = event.target as HTMLDetailsElement;

		if (menuItem.closed === details.open) {
			menuItem.closed = !details.open;
			this.#saveMenuState(menuItem.id, details.open);

			if (!details.open) {
				this.#recentlyCollapsed.add(menuItem.id);
			} else {
				this.#recentlyCollapsed.delete(menuItem.id);
			}

			if (details.open && this.singleOpenMode()) {
				this.#collapseOtherItems(this.menuItems(), menuItem);
			}

			this.menuItems.set([...this.menuItems()]);
		}
	}

	protected onSummaryClick(event: MouseEvent, menuItem: MenuItem) {
		if (event.ctrlKey) {
			this.#handleToggleAction(event, menuItem);
		}
	}

	protected onSummaryKeyboard(event: Event, menuItem: MenuItem) {
		this.#handleToggleAction(event, menuItem);
	}

	#handleToggleAction(event: Event, menuItem: MenuItem) {
		event.preventDefault();
		event.stopImmediatePropagation();
		if ((event as MouseEvent | KeyboardEvent).shiftKey) {
			this.#toggleSolo(menuItem);
		} else {
			this.#toggleAll(!!menuItem.closed);
		}
	}

	#toggleSolo(activeItem: MenuItem) {
		const willOpen = !!activeItem.closed;
		this.#updateItemsState(item => item === activeItem ? !willOpen : true);
	}

	#toggleAll(shouldOpen: boolean) {
		this.#updateItemsState(() => !shouldOpen);
	}

	#updateItemsState(calculateState: (item: MenuItem) => boolean) {
		const items = this.menuItems();
		items.forEach(item => {
			if (item.list?.length) {
				const shouldBeClosed = calculateState(item);
				if (item.closed !== shouldBeClosed) {
					item.closed = shouldBeClosed;
					this.#saveMenuState(item.id, !shouldBeClosed);
				}
			}
		});
		this.menuItems.set([...items]);
	}

	protected isMenuItemActive(menuItem: MenuItem): boolean {
		this.navigationEvent();
		const current = this.#routingService.currentState;
		const currentParams = current.params;
		const currentState = this.#routingService.appStates.find(elem => elem.url === current.url);
		const currentParentUrl = currentState?.parent?.url;

		const activeMatch = this.#isMatch(menuItem, current.url, currentParams);
		const hasActiveChild = menuItem.list?.some(child => this.#isMatch(child, current.url, currentParams)) ?? false;
		const isSameUrlAtTarget = current.url === menuItem.hrefObj.url || (menuItem.addHrefObj && current.url === menuItem.addHrefObj.url);

		if (isSameUrlAtTarget) {
			return activeMatch && (!menuItem.list?.length || menuItem.closed || !hasActiveChild);
		}

		if (currentParentUrl === menuItem.hrefObj.url) {
			return !!menuItem.closed && (menuItem.isActive?.(currentParams!) ?? true);
		}

		return false;
	}

	#isMatch(item: MenuItem, currentUrl: string, currentParams: Record<string, any> | undefined): boolean {
		const isSameUrl = currentUrl === item.hrefObj.url;
		const isSameAddUrl = !!item.addHrefObj && currentUrl === item.addHrefObj.url && (
			!item.addHrefObj.params || Object.entries(item.addHrefObj.params).every(([key, value]) => currentParams?.[key] == value)
		);
		return isSameAddUrl || (isSameUrl && (item.isActive?.(currentParams!) ?? true));
	}

	#loadMenuState(id: string): boolean {
		const saved = localStorage.getItem(`menu-state-${id}`);
		return saved ? JSON.parse(saved) : false;
	}

	#saveMenuState(id: string, isOpen: boolean) {
		localStorage.setItem(`menu-state-${id}`, JSON.stringify(isOpen));
	}

	#collapseOtherItems(items: MenuItem[], except: MenuItem) {
		items.forEach(item => {
			if (item !== except && item.list?.length && !item.closed) {
				item.closed = true;
				this.#saveMenuState(item.id, false);
			}
		});
	}

	#expandActiveSections(items: MenuItem[]) {
		let expandedParent: MenuItem | undefined;

		for (const item of items) {
			if (item.list?.length && item.closed && !this.#recentlyCollapsed.has(item.id)) {
				if (!this.#loadMenuState(item.id)) {
					continue;
				}
				const hasActiveChild = item.list.some(child => this.isMenuItemActive(child));
				if (hasActiveChild) {
					item.closed = false;
					this.#saveMenuState(item.id, true);
					expandedParent = item;
					break;
				}
			}
		}

		if (expandedParent) {
			if (this.singleOpenMode()) {
				this.#collapseOtherItems(items, expandedParent);
			}
			this.menuItems.set([...items]);
		}
	}

	async #updateMenuAsync(): Promise<void> {
		const items = await composeMenuItems(this.#menuItemsDefinition);
		this.menuItems.set(items);
	}

}
